import { IMersTransactionStatusDetail } from "../VaultInterfaces";

export type MersDiagnosticSeverity = "error" | "warning";

export interface IMersDiagnosticResult {
  severity: MersDiagnosticSeverity;
  text: string;
}

export interface IMersDiagnosticMessage {
  severity: MersDiagnosticSeverity;
  message: string;
}

function trimmedOrUndefined(value: string | null | undefined): string | undefined {
  if (value === null || value === undefined) {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length === 0 ? undefined : trimmed;
}

/**
 * MERS error/warning codes (see the 26.2 error appendix) are always 4+ digits. A 3-digit numeric
 * code is an HTTP transport status.
 *
 * EV-5306 (A-bis) fixed the backend (GetStatusDetailsFromPublishResult) so it no longer writes an
 * HTTP status into this field going forward - new transactions never produce a 3-digit code here.
 * This guard exists purely to handle transaction records persisted *before* that fix, which still
 * carry one. If a future reader is wondering why this check exists when the backend no longer
 * produces the condition it's checking for: that's why - it's defending against old data, not
 * current behavior.
 *
 * Anything else non-blank is treated as a MERS participant-defined code, which the backend's own
 * documentation warns is not guaranteed to be numeric.
 */
function isTransportStatusCode(code: string): boolean {
  return /^\d{3}$/.test(code);
}

interface IClassifiedDetail {
  severity: MersDiagnosticSeverity;
  description?: string;
  name?: string;
  code?: string;
  keyName?: string;
  keyValue?: string;
}

/**
 * Shared classification step for every reader of a MERS status detail: decides severity and
 * whether there's anything here worth showing at all, independent of how a given reader chooses
 * to format the result. Centralizing this fixed a real inconsistency (EV-5306 PR review,
 * kathrynyoung_FirstAm): before this module existed, the result dialog and getMersApiResponseError
 * each had their own copy of "is this detail an error," and they didn't agree - the dialog matched
 * only the exact string "error"/"warning", while getMersApiResponseError also recognized "failed".
 * For the same underlying data, the two could disagree on whether a detail counted as the error.
 *
 * Excludes "Success" and "Information" - both are real, server-constructed types (e.g. a
 * ChangeData response's <STATUS Condition="Success" Code="0" .../> on an otherwise-successful
 * transaction, or a Transfer notification's Informational reject condition) - neither is a problem
 * to report. Also excludes a detail with no type at all: nothing in the backend's own construction
 * paths can currently produce one, so this is a defensive-only guard, not a demonstrated case - but
 * unlike an unrecognized non-blank type string (which still folds into "error" below, so a real
 * problem is never silently discarded), a *missing* type isn't a condition we recognize at all, so
 * there's nothing to safely default it to.
 *
 * Absent context (blank description/name/code/key) is omitted rather than invented. A KEY value
 * with no KEY name can't be attributed to anything and could be a stray payload fragment, so it is
 * dropped rather than rendered unlabeled. A detail with nothing at all to show is dropped entirely.
 *
 * Returns null when the detail has no renderable content.
 */
function classifyDetail(detail: IMersTransactionStatusDetail | null | undefined): IClassifiedDetail | null {
  if (!detail) {
    return null;
  }

  const normalizedType = detail.type?.toLowerCase();
  if (!normalizedType || normalizedType === "success" || normalizedType === "information") {
    return null;
  }

  const description = trimmedOrUndefined(detail.description);
  const name = trimmedOrUndefined(detail.name);
  const code = trimmedOrUndefined(detail.code);
  const keyName = trimmedOrUndefined(detail.key);
  let keyValue = trimmedOrUndefined(detail.value);

  if (!keyName && keyValue) {
    keyValue = undefined;
  }

  if (!description && !name && !code && !keyName) {
    return null;
  }

  // Anything that isn't "warning" - "error" and any genuinely unrecognized (but non-blank) type -
  // folds into "error" so a real problem is never silently discarded, matching the EV-5306 AC's
  // requirement to preserve warning/error distinctions; the old `default: break` in the readers
  // this replaces dropped anything it didn't recognize.
  const severity: MersDiagnosticSeverity = normalizedType === "warning" ? "warning" : "error";

  return { severity, description, name, code, keyName, keyValue };
}

/**
 * Formats a single MERS transaction status detail for display in the result dialog
 * (VaultMersTransactionResultsModalPopup and its redux/Transfer-batch twin).
 *
 * Identification fallback is description -> name, per the EV-5306 acceptance criteria. Code is
 * always rendered separately (as a badge, or as a transport-status label for a historical 3-digit
 * HTTP code), so when description and name are both blank the code is not duplicated as a second,
 * redundant piece of text - the badge alone identifies the detail. The denying role is never
 * inferred from the code, and MERS wording/codes are shown verbatim rather than replaced with a
 * bespoke explanation (see EV-5306 for why this matters specifically for 5076 and 5941).
 *
 * Returns null when the detail has no renderable content.
 */
export function formatMersStatusDetail(
  detail: IMersTransactionStatusDetail | null | undefined
): IMersDiagnosticResult | null {
  const classified = classifyDetail(detail);
  if (!classified) {
    return null;
  }
  const { severity, description, name, code, keyName, keyValue } = classified;

  const message = description ?? name;

  const parts: string[] = [];
  if (code) {
    parts.push(isTransportStatusCode(code) ? `Transport error (HTTP ${code})` : `[${code}]`);
  }
  if (message) {
    parts.push(message);
  }

  let text = parts.join(" ");

  if (keyName) {
    const keyText = keyValue ? `${keyName}: ${keyValue}` : keyName;
    text = text ? `${text} (${keyText})` : `(${keyText})`;
  }

  return { severity, text };
}

/**
 * Identifies a single MERS transaction status detail for the plain-text, single-line error
 * captions in AddDocumentModal/ReverseDocumentModal (via getMersApiResponseError) - a different,
 * narrower UI than the result dialog, each handling exactly one document-level transaction rather
 * than a batch of loans. The AC that asks for the fuller code-badge/KEY display names only
 * VaultMersTransactionResultsModalPopup, so this intentionally stays at the identification message
 * only - description, then name, then code as the last resort (EV-5306 AC's literal fallback
 * order) - with no badge and no KEY suffix, matching what these two modals showed before this
 * change, modulo the severity-classification fix above.
 *
 * Returns null when the detail has no renderable content.
 */
export function getMersStatusDetailMessage(
  detail: IMersTransactionStatusDetail | null | undefined
): IMersDiagnosticMessage | null {
  const classified = classifyDetail(detail);
  if (!classified) {
    return null;
  }
  const message = classified.description ?? classified.name ?? classified.code;
  if (!message) {
    // Only reachable when classifyDetail's content check passed solely on keyName - there's no
    // KEY display in this plain-message format, so there's genuinely nothing left to show.
    return null;
  }
  return { severity: classified.severity, message };
}
